import { EMPTY, catchError, shareReplay, tap } from 'rxjs';
import { GlobalData, events$, ofAction } from '../event-store';
import { EventName, Interactions, SegmentProvider } from '../segment-provider';
import {
  csvExportFailed,
  csvExportSuccess,
  designerCsvExportClick,
  downloadClick,
  downloadFailed,
  downloadSuccess,
  generateDesignerCsvPlan,
  generateReportCsvPlan,
  generateViewDataCsvPlan,
  reportCsvExportClick,
  viewDataCsvExportClick,
} from '../events/csv-export';

function readableBytes(size: number): string {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return parseFloat((size / Math.pow(1024, i)).toFixed(2)) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

export function init(globalData: GlobalData, segment: SegmentProvider) {
  const stream$ = events$.pipe(shareReplay(1));

  stream$
    .pipe(
      ofAction(reportCsvExportClick, designerCsvExportClick),
      tap((ev) => {
        segment.track(EventName.UiClicked, {
          user_experience_id: segment.userExperienceId,
          action: Interactions.ExportCsv,
          chartType: 'table',
          context_id: ev.token,
          context: ev.action === reportCsvExportClick.action ? 'report' : 'visualization',
          location: 'chart',
        });
      }),
      catchError(() => EMPTY)
    )
    .subscribe();

  stream$
    .pipe(
      ofAction(viewDataCsvExportClick),
      tap((ev) => {
        segment.track(EventName.UiClicked, {
          user_experience_id: segment.userExperienceId,
          action: Interactions.ExportCsv,
          chartType: 'chart', // Actual chart type could be added if it's worth the effort
          context_id: ev.token,
          context: 'report',
          location: 'view-data',
        });
      }),
      catchError(() => EMPTY)
    )
    .subscribe();

  stream$
    .pipe(
      ofAction(generateDesignerCsvPlan, generateReportCsvPlan, generateViewDataCsvPlan),
      tap((ev) => {
        const location = ev.action === generateDesignerCsvPlan.action ? 'Viz Toolbar' : 'Report View';
        const context = ev.action === generateDesignerCsvPlan.action ? 'visualization' : 'report';
        const context_id = ev.action !== generateViewDataCsvPlan.action ? ev.chartToken : ev.switchViewToken;

        segment.track(EventName.UserExperienceMilestone, {
          user_experience_id: segment.userExperienceId,
          location,
          context,
          context_id,
          interaction: Interactions.ExportCsv,
          interaction_id: ev.switchViewToken,
          dataset_row_count: ev.datasetRowCount,
          dataset_column_count: ev.datasetColumnCount,
          table_row_count: ev.rowCount,
        });
      }),
      catchError(() => EMPTY)
    )
    .subscribe();

  stream$
    .pipe(
      ofAction(csvExportSuccess, csvExportFailed),
      tap((ev) => {
        segment.track(EventName.UserExperienceMilestone, {
          user_experience_id: segment.userExperienceId,
          location: 'chart',
          context: 'visualization',
          context_id: ev.token,
          interaction: Interactions.ExportCsv,
          interaction_id: ev.refId,
          is_retry: ev.isRetry,
          status: ev.action === csvExportSuccess.action ? 'success' : 'failure',
          failure_type: ev.action === csvExportFailed.action ? ev.err.message : undefined,
        });
      }),
      catchError(() => EMPTY)
    )
    .subscribe();

  stream$
    .pipe(
      ofAction(downloadClick),
      tap((ev) => {
        segment.track(EventName.UiClicked, {
          user_experience_id: segment.userExperienceId,
          action: 'download csv',
          chartType: 'table',
          context_id: ev.traceId,
          context: 'Download Drawer',
        });
      }),
      catchError(() => EMPTY)
    )
    .subscribe();

  stream$
    .pipe(
      ofAction(downloadSuccess, downloadFailed),
      tap((ev) => {
        segment.track(EventName.UserExperienceMilestone, {
          user_experience_id: segment.userExperienceId,
          location: 'Download Drawer',
          context: 'report',
          context_id: ev.traceId,
          interaction: Interactions.DownloadCsv,
          download_size: ev.action === downloadFailed.action ? 0 : ev.downloadSize,
          err: ev.action === downloadFailed.action ? ev.err : undefined,
        });
      }),
      catchError(() => EMPTY)
    )
    .subscribe();
}
